import React, { useMemo } from 'react';
import { Accordion, Highlight, Text } from '@mantine/core';

import { useOrganisationSearch } from '../../context/OrganisationSearchContext';
import stringifyNumber from '../../utility/stringifyNumber';
import { PropagationStopper } from '../common';
import ProductCategories from './ProductCategories';
import { Manager } from '../../codegen/graphql';

type Props = {
    manager: Manager;
};

function ProcurementManagerCard({ manager }: Props) {
    const { organisationSearch } = useOrganisationSearch();
    const { fullName, speedDial, phoneNo, email, designation } = useMemo(() => {
        const number =
            stringifyNumber(manager.telNo) ||
            stringifyNumber(manager.mobileNo) ||
            'N/A';

        return {
            fullName: `${manager.firstName} ${manager.lastName}` || 'N/A',
            designation: manager.designation || 'N/A',
            speedDial: manager.speedDial || 'N/A',
            phoneNo: number,
            email: manager.email || 'N/A',
        };
    }, [manager]);

    if (!manager?.id) {
        return null;
    }

    return (
        <Accordion.Item value={manager.id ?? ''}>
            <Accordion.Control>
                <div className="grid grid-cols-24 w-full gap-1 items-center">
                    <div className="flex justify-between col-span-6">
                        <div className="flex flex-col gap-1 pl-3 overflow-hidden">
                            <Text className="font-bold uppercase">
                                <Highlight
                                    className="font-bold text-md"
                                    highlight={organisationSearch}
                                >
                                    {fullName}
                                </Highlight>
                            </Text>
                            <Text className="text-sm whitespace-nowrap text-ellipsis max-w-full">
                                <Highlight
                                    className="text-sm"
                                    highlight={organisationSearch}
                                >
                                    {designation}
                                </Highlight>
                            </Text>
                        </div>
                    </div>

                    <div className="flex col-span-5 ml-8">
                        <Highlight
                            className="text-sm"
                            highlight={organisationSearch}
                        >
                            {speedDial}
                        </Highlight>
                    </div>

                    <div className="flex col-span-6 ml-8 overflow-hidden">
                        <PropagationStopper>
                            <a
                                href={`tel:${phoneNo}`}
                                type="tel"
                                className="hover:underline text-center whitespace-nowrap text-ellipsis overflow-hidden max-w-full text-sm"
                            >
                                <Highlight
                                    className="text-sm"
                                    highlight={organisationSearch}
                                >
                                    {phoneNo}
                                </Highlight>
                            </a>
                        </PropagationStopper>
                    </div>

                    <div className="flex col-span-6 ml-8 overflow-hidden">
                        <PropagationStopper>
                            <a
                                href={`mailto:${email}`}
                                type="email"
                                className="hover:underline whitespace-nowrap text-ellipsis overflow-hidden text-sm"
                            >
                                <Highlight
                                    className="text-sm"
                                    highlight={organisationSearch}
                                >
                                    {email || 'N/A'}
                                </Highlight>
                            </a>
                        </PropagationStopper>
                    </div>
                </div>
            </Accordion.Control>
            <Accordion.Panel>
                <ProductCategories categories={manager.categories ?? []} />
            </Accordion.Panel>
        </Accordion.Item>
    );
}

export default ProcurementManagerCard;
